// The Staff page's pure helpers. Pure: no DOM.
import type { AdminSchemas } from "@10twenty/api/admin";
import { roleLabel, type StaffRole } from "./nav";

export type StaffEntry = AdminSchemas["StaffListEntry"];
export type StaffSession = AdminSchemas["StaffSession"];

export function passkeysLabel(count: number): string {
  if (count === 0) return "None yet";
  return count === 1 ? "1 passkey" : `${count} passkeys`;
}

/** A pending enrolment link's state, or "—". */
export function enrolmentLabel(enrolment: StaffEntry["enrolment"], now = Date.now()): string {
  if (!enrolment) return "—";
  const left = new Date(enrolment.expires_at).getTime() - now;
  if (left <= 0) return "Link expired";
  return `Link open, ${Math.ceil(left / 60_000)} min left`;
}

/** What a grant does, for its confirmation. */
export function grantConfirmText(account: string, role: StaffRole, current?: StaffEntry): string {
  if (!current)
    return `Give ${account} the ${roleLabel(role)} role? They need an enrolment link before they can sign in here.`;
  return `Change ${current.email} from ${roleLabel(current.role)} to ${roleLabel(role)}? It applies on their next request.`;
}

/** The member a grant form's account (email or id) names, if already staff. */
export function staffFor(staff: readonly StaffEntry[], account: string): StaffEntry | undefined {
  const wanted = account.trim().toLowerCase();
  return staff.find((member) => member.user_id.toLowerCase() === wanted || member.email.toLowerCase() === wanted);
}

/** Every live session with its member, newest first. */
export function allSessions(staff: readonly StaffEntry[]): { member: StaffEntry; session: StaffSession }[] {
  return staff
    .flatMap((member) => member.sessions.map((session) => ({ member, session })))
    .sort((a, b) => b.session.created_at.localeCompare(a.session.created_at));
}
